import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatPrice, MEDIA, MENU_CATEGORIES } from "../lib/api";
import { useCart } from "../context/CartContext";
import { Plus } from "lucide-react";

export default function OrderPage() {
  const [items, setItems] = useState([]);
  const [active, setActive] = useState(MENU_CATEGORIES[0].key);
  const { add, items: cart, subtotal, setOpen, clear } = useCart();
  const [step, setStep] = useState("menu"); // menu | checkout | done
  const [order, setOrder] = useState(null);
  const [form, setForm] = useState({
    customer_name: "",
    customer_email: "",
    customer_phone: "",
    fulfillment: "pickup",
    address: "",
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/menu").then((r) => setItems(r.data)).catch(() => {});
  }, []);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const placeOrder = async (e) => {
    e.preventDefault();
    if (cart.length === 0) {
      toast.error("Your cart is empty");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/orders", {
        ...form,
        items: cart.map((i) => ({
          menu_item_id: i.menu_item_id,
          name: i.name,
          price: i.price,
          quantity: i.quantity,
        })),
      });
      setOrder(data);
      clear();
      setStep("done");
      toast.success("Order placed");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not place order");
    } finally {
      setSubmitting(false);
    }
  };

  if (step === "done" && order) {
    return (
      <div data-testid="order-confirmation" className="pt-40 pb-24 max-w-[700px] mx-auto px-6 text-center">
        <div className="overline mb-6">Order received</div>
        <h1 className="font-display text-5xl text-[#F9F6F0]">Thank you, {order.customer_name}.</h1>
        <p className="mt-6 text-[#9AA894] leading-relaxed">
          We are preparing your order. A confirmation has been sent to{" "}
          <span className="text-[#D4C1A0]">{order.customer_email}</span>.
        </p>
        <p className="mt-4 text-xs text-[#9AA894] tracking-widest uppercase">
          Reference · {order.id.slice(0, 8).toUpperCase()} · {formatPrice(order.subtotal)}
        </p>
      </div>
    );
  }

  if (step === "checkout") {
    return (
      <div data-testid="order-checkout" className="pt-32 pb-24 max-w-[800px] mx-auto px-6 md:px-12">
        <button onClick={() => setStep("menu")} className="overline text-[10px] hover:text-[#D4C1A0]" data-testid="order-back-to-menu">
          ← Back to menu
        </button>
        <h1 className="font-display text-5xl text-[#F9F6F0] mt-8">Checkout</h1>

        <div className="mt-12 border-t border-[#2E3628] pt-8 space-y-3">
          {cart.map((i) => (
            <div key={i.menu_item_id} className="flex justify-between text-sm">
              <span className="text-[#F9F6F0]">{i.quantity} × {i.name}</span>
              <span className="text-[#D4C1A0]">{formatPrice(i.price * i.quantity)}</span>
            </div>
          ))}
          <div className="flex justify-between pt-4 border-t border-[#2E3628] mt-4">
            <span className="overline">Subtotal</span>
            <span className="font-display text-2xl text-[#F9F6F0]" data-testid="checkout-subtotal">{formatPrice(subtotal)}</span>
          </div>
        </div>

        <form onSubmit={placeOrder} className="mt-12 space-y-8" data-testid="checkout-form">
          <div className="grid md:grid-cols-2 gap-x-8 gap-y-8">
            <div>
              <label className="overline">Name</label>
              <input data-testid="checkout-name" required className="input-line mt-2" value={form.customer_name} onChange={(e) => update("customer_name", e.target.value)} />
            </div>
            <div>
              <label className="overline">Email</label>
              <input data-testid="checkout-email" type="email" required className="input-line mt-2" value={form.customer_email} onChange={(e) => update("customer_email", e.target.value)} />
            </div>
            <div>
              <label className="overline">Phone</label>
              <input data-testid="checkout-phone" required className="input-line mt-2" value={form.customer_phone} onChange={(e) => update("customer_phone", e.target.value)} />
            </div>
            <div>
              <label className="overline">Fulfillment</label>
              <select data-testid="checkout-fulfillment" className="input-line mt-2 appearance-none bg-[#0F120E]" value={form.fulfillment} onChange={(e) => update("fulfillment", e.target.value)}>
                <option value="pickup">Pickup</option>
                <option value="delivery">Delivery</option>
              </select>
            </div>
            {form.fulfillment === "delivery" && (
              <div className="md:col-span-2">
                <label className="overline">Address</label>
                <input data-testid="checkout-address" required className="input-line mt-2" value={form.address} onChange={(e) => update("address", e.target.value)} />
              </div>
            )}
            <div className="md:col-span-2">
              <label className="overline">Notes (optional)</label>
              <textarea data-testid="checkout-notes" className="input-line mt-2" rows={3} value={form.notes} onChange={(e) => update("notes", e.target.value)} />
            </div>
          </div>
          <button type="submit" disabled={submitting} className="btn-solid" data-testid="checkout-submit-btn">
            {submitting ? "Placing order…" : "Place order"}
          </button>
        </form>
      </div>
    );
  }

  const filtered = items.filter((i) => i.category === active);

  return (
    <div data-testid="order-page" className="pt-32 pb-32">
      <section className="relative h-[36vh] min-h-[280px] flex items-end overflow-hidden">
        <img src={MEDIA.dish} alt="Order" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0F120E]/40 to-[#0F120E]" />
        <div className="relative max-w-[1100px] mx-auto px-6 md:px-12 pb-12 z-10 w-full">
          <div className="overline mb-4">Order online</div>
          <h1 className="font-display text-5xl sm:text-6xl text-[#F9F6F0]">
            Bring CHICA home.
          </h1>
        </div>
      </section>

      <section className="max-w-[1100px] mx-auto px-6 md:px-12 py-16">
        <div className="flex flex-wrap gap-3 mb-12">
          {MENU_CATEGORIES.map((c) => (
            <button
              key={c.key}
              data-testid={`order-tab-${c.key}`}
              onClick={() => setActive(c.key)}
              className={`px-4 py-2 text-[11px] uppercase tracking-[0.2em] border transition-colors ${
                active === c.key
                  ? "border-[#D4C1A0] text-[#D4C1A0]"
                  : "border-[#2E3628] text-[#9AA894] hover:text-[#F9F6F0]"
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>

        <div className="space-y-1">
          {filtered.map((item) => (
            <div
              key={item.id}
              data-testid={`order-item-${item.id}`}
              className="flex items-center py-6 border-b border-[#2E3628]/60 gap-6"
            >
              <div className="flex-1">
                <h3 className="font-display text-2xl text-[#F9F6F0]">{item.name}</h3>
                <p className="text-[#9AA894] text-sm mt-2 max-w-2xl leading-relaxed">{item.description}</p>
              </div>
              <div className="font-display text-xl text-[#D4C1A0] whitespace-nowrap">
                {formatPrice(item.price)}
              </div>
              <button
                data-testid={`order-add-${item.id}`}
                onClick={() => {
                  add(item);
                  toast.success(`${item.name} added`);
                }}
                className="border border-[#D4C1A0] text-[#D4C1A0] hover:bg-[#D4C1A0] hover:text-[#0F120E] transition-colors p-3"
                aria-label={`Add ${item.name}`}
              >
                <Plus className="w-4 h-4" strokeWidth={1.4} />
              </button>
            </div>
          ))}
        </div>

        {cart.length > 0 && (
          <div className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-[#1C2219] border border-[#2E3628] flex items-center gap-6 px-6 py-3 z-30">
            <span className="text-sm text-[#F9F6F0]" data-testid="floating-cart-count">
              {cart.reduce((s, i) => s + i.quantity, 0)} items · <span className="text-[#D4C1A0]">{formatPrice(subtotal)}</span>
            </span>
            <button onClick={() => setOpen(true)} data-testid="floating-view-cart" className="text-[11px] uppercase tracking-widest text-[#9AA894] hover:text-[#F9F6F0]">View</button>
            <button onClick={() => setStep("checkout")} data-testid="floating-checkout" className="btn-solid py-2 px-4 text-[11px]">
              Checkout
            </button>
          </div>
        )}
      </section>
    </div>
  );
}
